import axios from "axios";
import alert_mixin from "@/utils/mixins/alert_mixin";
import NavigationBackButton from "@/components/probaworkflows_base_components/NavigationBackButton/NavigationBackButton.vue";
import ConditionQuestionAssignment from "@/components/probaworkflows_base_components/ConditionQuestionAssignement/ConditionQustionAssignment.vue";
import {
  GET_RULE_CONDITIONS_URL,
  ADD_RULE_CONDITION_URL,
  UPDATE_RULE_CONDITION_URL,
  DELETE_RULE_CONDITION_URL,
  GET_COMPLIANCE_DROPDOWNS_URL,
  GET_CSRF_TOKEN,
  GET_COMPLIANCE_FIELDS_URL,
  GET_RESULT_ACTION_QUESTIONS_URL,
  GET_CHECKLIST_ITEMS_URL,
} from "@/utils/constants/constants";
import "datatables.net";
import "datatables.net-dt/css/dataTables.dataTables.css";
import $ from "jquery";

export default {
  name: "ConditionManagement",
  mixins: [alert_mixin],
  components: { NavigationBackButton, ConditionQuestionAssignment },
  data: () => ({
    token: null,
    csrfToken: "",
    selected_rule: null,
    rule_conditions: [],
    is_loading_conditions: false,
    is_deleting: false,
    current_form_id: null,
    current_form_name: "",
    condition_form: {
      id: null,
      rule_id: null,
      condition_type: "",
      operator: "",
      value: "",
      comparison_value: "",
      logical_operator: "AND",
      selected_options: [],
      compliance_field: null,
      result_action_question: null,
      checklist_item_assignment: null,
      version: 1,
    },
    errors: {},
    is_saving_condition: false,
    condition_to_delete: null,
    conditionModal: null,
    deleteConditionModal: null,
    condition_type_options: [],
    operator_options: [],
    compliance_fields: [],
    result_action_questions: [],
    checklist_items: [],
    show_assign_modal: false,
    entity_type_id: null,
    condition_for_assignment: null,
    dataTable: null,
  }),
  mounted() {
    this.$nextTick(() => this.initialize_modals());
    this.current_form_id = this.$route.query.form_id || sessionStorage.getItem("current_form_id");
    this.current_form_name = this.$route.query.form_name || sessionStorage.getItem("current_form_name");
    const rule_id = this.$route.query.rule_id || sessionStorage.getItem("current_rule_id");
    const rule_name = this.$route.query.rule_name || sessionStorage.getItem("current_rule_name");
    
    if (rule_id) {
      this.selected_rule = { id: rule_id, name: rule_name };
      this.condition_form.rule_id = rule_id;
      
      this.entity_type_id = this.$route.query.entity_type_id || sessionStorage.getItem("entity_type_id");
      
      this.initializeToken().then(() => {
        this.fetch_all_dropdowns().then(() => this.get_rule_conditions(rule_id))
          .catch(() => this.get_rule_conditions(rule_id));
      }).catch(() => this.show_error("Failed to initialize token"));
    } else {
      this.show_error("No rule selected. Please go back and select a rule.");
    }
  },
  computed: {
    computed_headers() {
      const headers = { "Content-Type": "application/json" };
      if (this.token) headers.Authorization = `Token ${this.token}`;
      if (this.csrfToken) headers["X-CSRFToken"] = this.csrfToken;
      return headers;
    },
    table_safe_conditions() {
      return this.rule_conditions.map(condition => ({
        ...condition,
        value: condition.value || "-",
        comparison_value: condition.comparison_value || "-",
        selected_options: condition.selected_options || [],
        logical_operator: condition.logical_operator || "AND",
        version: condition.version || 1,
      }));
    },
    condition_types() {
      return this.condition_type_options.length > 0 
        ? this.condition_type_options.map(type => ({ value: type.value, label: type.label }))
        : [
            { value: "1", label: "Question Response" },
            { value: "2", label: "Time Based" },
            { value: "3", label: "User Role" },
            { value: "4", label: "Checklist Item" },
            { value: "5", label: "Status Based" }
          ];
    },
    filtered_operators() {
      return this.operator_options.length > 0 
        ? this.operator_options.map(op => ({ value: op.value, label: op.label }))
        : this.get_default_operators();
    },
    logical_operators() {
      return [
        { value: "AND", label: "AND (All conditions must be true)" },
        { value: "OR", label: "OR (At least one condition must be true)" },
        { value: "NOT", label: "NOT (Condition must be false)" },
        { value: "XOR", label: "XOR (Exactly one condition must be true)" },
        { value: "NAND", label: "NAND (Not all conditions true)" }
      ];
    },
    generate_condition_summary() {
      if (this.rule_conditions.length === 0) return "No conditions defined.";
      return this.rule_conditions.map((condition, index) => {
        const conditionType = this.condition_type_options.find(ct => ct.value == condition.condition_type);
        const operator = this.operator_options.find(op => op.value == condition.operator);
        const conditionTypeLabel = conditionType?.label || "Unknown";
        const operatorLabel = operator?.label || "Unknown";
        
        let conditionStr = `IF ${conditionTypeLabel} ${operatorLabel} `;
        
        if (condition.selected_options && condition.selected_options.length > 0) {
          const labels = condition.selected_options.map(o => typeof o === 'object' ? o.label : o);
          conditionStr += `[${labels.join(', ')}]`;
        } else if (condition.value) {
          conditionStr += `"${condition.value}"`;
          if (condition.comparison_value) {
            conditionStr += ` to "${condition.comparison_value}"`;
          }
        }
        
        if (index > 0) {
          conditionStr = ` ${condition.logical_operator || 'AND'} ${conditionStr}`;
        }
        return conditionStr;
      }).join('\n') + '\nTHEN Rule is triggered';
    },
    should_show_value_max() {
      if (!this.condition_form.operator) return false;
      const operatorStr = this.get_operator_string(this.condition_form.operator);
      return operatorStr === 'between' || this.condition_form.operator === '7';
    },
    should_show_multi_select() {
      if (!this.condition_form.operator) return false;
      const operatorStr = this.get_operator_string(this.condition_form.operator);
      return operatorStr === 'in' || operatorStr === 'not_in' || 
             this.condition_form.operator === '12' || this.condition_form.operator === '13';
    },
    should_show_compliance_field() {
      const conditionTypeStr = this.get_condition_type_string(this.condition_form.condition_type);
      return conditionTypeStr === 'question_response' || this.condition_form.condition_type === '1';
    },
    should_show_result_action_question() {
      const conditionTypeStr = this.get_condition_type_string(this.condition_form.condition_type);
      return conditionTypeStr === 'question_response' || this.condition_form.condition_type === '1';
    },
    should_show_checklist_item() {
      const conditionTypeStr = this.get_condition_type_string(this.condition_form.condition_type);
      return conditionTypeStr === 'checklist_item' || this.condition_form.condition_type === '4';
    },
  },
  methods: {
    initialize_data_table() {
      const table_id = "#condition_management_table";

      this.$nextTick(() => {
        const table_element = document.querySelector(table_id);
        if (!table_element) return;

        if ($.fn.DataTable.isDataTable(table_id)) {
          $(table_id).DataTable().destroy();
        }

        this.dataTable = $(table_id).DataTable({
          responsive: true,
          autoWidth: false,
          pageLength: 10,
          order: [[9, 'desc']],
          columnDefs: [
            { orderable: false, targets: [10] },
            { searchable: false, targets: [10] },
          ],
          language: {
            emptyTable: "No conditions found",
            zeroRecords: "No matching conditions found",
            info: "Showing _START_ to _END_ of _TOTAL_ conditions",
            infoEmpty: "Showing 0 to 0 of 0 conditions",
            infoFiltered: "(filtered from _MAX_ total conditions)",
            search: "Search:",
            paginate: {
              first: "First",
              last: "Last",
              next: "Next",
              previous: "Previous"
            }
          }
        });
      });
    },

    async initializeToken() {
      this.token = sessionStorage.getItem("token") || localStorage.getItem("token");
      await this.fetch_csrf_token();
    },

    fetch_csrf_token () {
      return axios
        .get(GET_CSRF_TOKEN, {
          headers: { "Content-Type": "application/json" }
        })
        .then((response) => {
          const data = response?.data || {};
          this.csrfToken =
            data.csrf_token ||
            data.token ||
            data.csrfToken ||
            this.get_csrf_from_cookie() ||
            "";
        })
        .catch(() => {
          this.csrfToken = this.get_csrf_from_cookie() || "";
        });
    },

    get_csrf_from_cookie() {
      const name = "csrftoken";
      if (!document.cookie) return null;
      return document.cookie.split(';').reduce((result, cookie) => {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith(name + "=")) {
          return decodeURIComponent(trimmedCookie.substring(name.length + 1));
        }
        return result;
      }, null);
    },

    fetch_all_dropdowns() {
      return this.fetch_compliance_dropdowns()
        .then(() => {
          if (this.current_form_id) {
            return this.fetch_compliance_fields();
          }
          return Promise.resolve();
        })
        .then(() => {
          return this.fetch_result_action_questions();
        })
        .then(() => {
          return this.fetch_checklist_items();
        })
        .catch(() => {
          this.show_error("Failed to load some dropdown data");
        });
    },

    fetch_compliance_dropdowns() {
      return axios
        .get(GET_COMPLIANCE_DROPDOWNS_URL, {
          headers: this.computed_headers
        })
        .then((response) => {
          if (response.data.status === "success") {
            this.condition_type_options = (response.data.data.condition_types || []).map(ct => ({
              value: ct.id,
              label: this.format_label(ct.name)
            }));

            this.operator_options = (response.data.data.operators || []).map(op => ({
              value: op.id,
              label: this.format_label(op.operator)
            }));
          } else {
            this.show_error(response.data.message || "Failed to load dropdown data");
          }
        })
        .catch((error) => {
          this.show_error("Could not load dropdown options: " + error.message);
        });
    },

    fetch_compliance_fields() {
      return axios
        .get(`${GET_COMPLIANCE_FIELDS_URL}?form_id=${this.current_form_id}`, {
          headers: this.computed_headers
        })
        .then((response) => {
          if (response.data.status === "success") {
            this.compliance_fields = response.data.data || response.data.fields || [];
          }
        })
        .catch(() => {
          this.compliance_fields = [];
        });
    },

    fetch_result_action_questions() {
      return axios
        .get(GET_RESULT_ACTION_QUESTIONS_URL, {
          headers: this.computed_headers
        })
        .then((response) => {
          if (response.data.status === "success") {
            this.result_action_questions = response.data.data || response.data.questions || [];
          }
        })
        .catch(() => {
          this.result_action_questions = [];
        });
    },

    fetch_checklist_items() {
      return axios
        .get(GET_CHECKLIST_ITEMS_URL, {
          headers: this.computed_headers
        })
        .then((response) => {
          if (response.data.status === "success") {
            this.checklist_items = response.data.data || response.data.items || [];
          }
        })
        .catch(() => {
          this.checklist_items = [];
        });
    },

    get_default_operators() {
      const conditionType = this.get_condition_type_string(this.condition_form.condition_type);
      const operatorSets = {
        question_response: [
          { value: "1", label: "Equals" },
          { value: "2", label: "Not Equals" },
          { value: "3", label: "Greater Than" },
          { value: "4", label: "Less Than" },
          { value: "5", label: "Greater Or Equal" },
          { value: "6", label: "Less Or Equal" },
          { value: "8", label: "Contains" },
          { value: "9", label: "Not Contains" },
          { value: "10", label: "Starts With" },
          { value: "11", label: "Ends With" },
          { value: "12", label: "In" },
          { value: "13", label: "Not In" }
        ],
        time_based: [
          { value: "1", label: "Equals" },
          { value: "2", label: "Not Equals" },
          { value: "3", label: "Greater Than" },
          { value: "4", label: "Less Than" },
          { value: "5", label: "Greater Or Equal" },
          { value: "6", label: "Less Or Equal" },
          { value: "7", label: "Between" }
        ],
        user_role: [
          { value: "1", label: "Equals" },
          { value: "2", label: "Not Equals" },
          { value: "8", label: "Contains" },
          { value: "9", label: "Not Contains" },
          { value: "12", label: "In" },
          { value: "13", label: "Not In" }
        ],
        checklist_item: [
          { value: "1", label: "Equals" },
          { value: "2", label: "Not Equals" }
        ],
        status_based: [
          { value: "1", label: "Equals" },
          { value: "2", label: "Not Equals" },
          { value: "12", label: "In" },
          { value: "13", label: "Not In" }
        ]
      };
      return Reflect.get(operatorSets, conditionType) || [];
    },

    get_condition_type_string(typeId) {
      if (typeof typeId === 'string' && !/^\d+$/.test(typeId)) return typeId;
      return this.get_condition_type_label(typeId);
    },

    get_operator_string(operatorId) {
      if (typeof operatorId === 'string' && !/^\d+$/.test(operatorId)) return operatorId;
      const label = this.get_operator_label(operatorId);
      return label ? label.toLowerCase().replace(/[^a-z0-9]/g, '_') : operatorId;
    },

    get_condition_type_class(type) {
      const typeId = this.get_condition_type_string(type);
      const classMap = {
        "question_response": "bg-primary",
        "time_based": "bg-warning text-dark",
        "user_role": "bg-info",
        "checklist_item": "bg-purple",
        "status_based": "bg-success"
      };
      return Reflect.get(classMap, typeId) || "bg-secondary";
    },

    get_logical_operator_class(operator) {
      const classMap = {
        "AND": "bg-success",
        "OR": "bg-warning text-dark",
        "NOT": "bg-danger",
        "XOR": "bg-info",
        "NAND": "bg-purple"
      };
      return Reflect.get(classMap, operator) || "bg-secondary";
    },

    format_condition_type(type) {
      const label = this.get_condition_type_label(type);
      if (label) {
        return label.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      }
      return String(type);
    },

    format_operator(operator) {
      return this.get_operator_label(operator) || String(operator);
    },

    format_date(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    format_selected_options(options) {
    if (!options || !Array.isArray(options) || options.length === 0) {
      return null;
    }

    return options.map(opt => {
      if (typeof opt === 'object' && opt !== null) {
        return opt.label || opt.value || "Unknown";
      }
      return opt;
    }).join(', ');
  },

    get_field_item_display(condition) {
      if (condition.field_assignments && condition.field_assignments.length > 0) {
        return condition.field_assignments
          .map(assign => assign.question_text || `Question ${assign.compliance_field}`)
          .join(' + ');
      }

      if (condition.compliance_field) {
        const field = this.compliance_fields.find(f => f.id == condition.compliance_field);
        return field ? (field.name || field.field_name || `Field ${field.id}`) : '';
      }
      
      return '';
    },

    format_label(text) {
      if (!text) return "";
      return text.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    },

    get_condition_type_label(typeId) {
      const type = this.condition_type_options.find(t => t.value == typeId);
      return type ? type.label : typeId;
    },

    get_operator_label(operatorId) {
      const operator = this.operator_options.find(o => o.value == operatorId);
      return operator ? operator.label : operatorId;
    },

    initialize_modals() {
      const modalConfigs = [
        { ref: "conditionModal", prop: "conditionModal" },
        { ref: "deleteConditionModal", prop: "deleteConditionModal" }
      ];
      modalConfigs.forEach(({ ref, prop }) => {
        const el = Reflect.get(this.$refs, ref);
        if (el && window.bootstrap) {
          Reflect.set(this, prop, new window.bootstrap.Modal(el, { backdrop: true, keyboard: true }));
        }
      });
    },

    open_add_condition_modal() { 
      this.reset_condition_form(); 
      if (this.conditionModal) this.conditionModal.show(); 
    },

    open_edit_condition_modal(condition) {
      this.condition_form = { 
        id: condition.id,
        rule_id: condition.rule_id,
        condition_type: condition.condition_type,
        operator: condition.operator,
        value: condition.value || "",
        comparison_value: condition.comparison_value || "",
        logical_operator: condition.logical_operator || "AND",
        selected_options: condition.selected_options || [],
        compliance_field: condition.compliance_field || null,
        result_action_question: condition.result_action_question || null,
        checklist_item_assignment: condition.checklist_item_assignment || null,
        version: condition.version || 1,
      };
      if (this.conditionModal) this.conditionModal.show();
    },

    open_assign_question_modal(condition) {
       this.condition_for_assignment = condition;
        this.show_assign_modal = true;
    },
    
    close_assign_questions_modal() {
      this.show_assign_modal = false;
      this.condition_for_assignment = null;
      this.entity_type_id = null;
    },
    
    on_assignment_updated() {
      this.show_success("Question assigned successfully");
      if (this.selected_rule?.id) {
        this.get_rule_conditions(this.selected_rule.id);
      }
      this.close_assign_questions_modal();
    },

    close_condition_modal() { 
      if (this.conditionModal) this.conditionModal.hide(); 
      this.reset_condition_form(); 
    },

    close_delete_modal() { 
      if (this.deleteConditionModal) this.deleteConditionModal.hide(); 
      this.condition_to_delete = null; 
    },

    on_condition_type_change() {
      this.condition_form.operator = "";
      this.condition_form.value = "";
      this.condition_form.comparison_value = "";
      this.condition_form.selected_options = [];
      this.condition_form.compliance_field = null;
      this.condition_form.result_action_question = null;
      this.condition_form.checklist_item_assignment = null;
      this.errors = {};
    },

    on_operator_change() {
      this.errors = {};
    },

  async get_rule_conditions(ruleId) {
      this.is_loading_conditions = true;
      this.rule_conditions = [];
      try {
        const response = await axios.get(`${GET_RULE_CONDITIONS_URL}?rule_id=${ruleId}`, { headers: this.computed_headers });
        if (response.data.status === "success") {
          const conditions = (response.data.conditions || []).map(condition => ({
            id: condition.id,
            rule_id: condition.compliance_rule,
            condition_type: condition.condition_type,
            operator: condition.condition_operator,
            value: condition.value || '',
            comparison_value: condition.value_max || '',
            selected_options: condition.selected_options || [],
            logical_operator: "AND",
            compliance_field: condition.compliance_field || null,
            result_action_question: condition.result_action_question || null,
            checklist_item_assignment: condition.checklist_item_assignment || null,
            version: condition.version || 1,
            created_at: condition.date_created,
            updated_at: condition.date_created,
          }));
          
          this.rule_conditions = conditions.sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return dateB - dateA;
          });
          
          this.$nextTick(() => {
            this.initialize_data_table();
          });
        } else {
          this.show_error(response.data.message || "Failed to load conditions");
        }
      } catch (error) {
        this.show_error("Error fetching conditions: " + error.message);
      } finally {
        this.is_loading_conditions = false;
      }
    },

    validate_form() {
      this.errors = {};
      
      if (!this.condition_form.condition_type) {
        this.errors.condition_type = "Condition type is required";
      }
      
      if (!this.condition_form.operator) {
        this.errors.operator = "Operator is required";
      }
      
      const operatorStr = this.get_operator_string(this.condition_form.operator);
      const isInOperator = operatorStr === 'in' || operatorStr === 'not_in' || 
                          this.condition_form.operator === '12' || this.condition_form.operator === '13';
      
      if (isInOperator) {
        if (!this.condition_form.selected_options || this.condition_form.selected_options.length === 0) {
          this.errors.selected_options = "At least one option is required";
        }
      }
      
      return Object.keys(this.errors).length === 0;
    },

    async save_condition() {
      if (this.is_saving_condition || !this.validate_form() || !this.csrfToken) return;
      this.is_saving_condition = true;
      try {
        const payload = {
          compliance_rule: parseInt(this.condition_form.rule_id, 10),
          condition_type: parseInt(this.condition_form.condition_type, 10),
          condition_operator: parseInt(this.condition_form.operator, 10),
          version: parseInt(this.condition_form.version, 10) || 1,
        };
        
        if (this.condition_form.value) {
          payload.value = this.condition_form.value;
        }
        
        if (this.condition_form.comparison_value) {
          payload.value_max = this.condition_form.comparison_value;
        }
        
        if (this.condition_form.selected_options && this.condition_form.selected_options.length > 0) {
          payload.selected_options = this.condition_form.selected_options;
        }
        
        if (this.condition_form.compliance_field) {
          payload.compliance_field = parseInt(this.condition_form.compliance_field, 10);
        }
        
        if (this.condition_form.result_action_question) {
          payload.result_action_question = parseInt(this.condition_form.result_action_question, 10);
        }
        
        if (this.condition_form.checklist_item_assignment) {
          payload.checklist_item_assignment = parseInt(this.condition_form.checklist_item_assignment, 10);
        }
        
        if (this.condition_form.id) {
          payload.id = this.condition_form.id;
        }
        
        const url = this.condition_form.id ? UPDATE_RULE_CONDITION_URL : ADD_RULE_CONDITION_URL;
        const response = await axios.post(url, payload, { headers: this.computed_headers });
        
        if (response.data.status === "success") {
          this.show_success("Condition saved successfully");
          this.close_condition_modal();
          await this.get_rule_conditions(this.selected_rule.id);
        } else {
          this.show_error(response.data.message || "Failed to save condition");
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.response?.data?.errors || error.message || "Failed to save condition";
        this.show_error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      } finally {
        this.is_saving_condition = false;
      }
    },

    reset_condition_form() {
      this.condition_form = {
        id: null,
        rule_id: this.selected_rule?.id || null,
        condition_type: "",
        operator: "",
        value: "",
        comparison_value: "",
        logical_operator: "AND",
        selected_options: [],
        compliance_field: null,
        result_action_question: null,
        checklist_item_assignment: null,
        version: 1,
      };
      this.errors = {};
    },

    confirm_delete_condition(condition) { 
      this.condition_to_delete = condition; 
      if (this.deleteConditionModal) this.deleteConditionModal.show(); 
    },

    async delete_condition() {
      if (!this.condition_to_delete || !this.csrfToken) return;
      this.is_deleting = true;
      try {
        const response = await axios.post(DELETE_RULE_CONDITION_URL, { id: this.condition_to_delete.id }, { headers: this.computed_headers });
        if (response.data.status === "success") {
          this.show_success("Condition deleted successfully");
          this.close_delete_modal();
          await this.get_rule_conditions(this.selected_rule.id);
        } else {
          this.show_error(response.data.message || "Failed to delete condition");
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.response?.data?.errors || error.message || "Failed to delete condition";
        this.show_error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      } finally {
        this.is_deleting = false;
      }
    },

    get_value_placeholder() {
      const conditionTypeStr = this.get_condition_type_string(this.condition_form.condition_type);
      const operatorStr = this.get_operator_string(this.condition_form.operator);
      
      const placeholders = {
        'question_response': 'Enter expected answer',
        'time_based': 'YYYY-MM-DD or number of days',
        'user_role': 'Enter role name',
        'checklist_item': 'Enter status (e.g., completed, pending)',
        'status_based': 'Enter status value'
      };
      
      if (['in', 'not_in'].includes(operatorStr)) {
        return 'Comma-separated values';
      }
      
      return Reflect.get(placeholders, conditionTypeStr) || 'Enter value';
    },

    get_value_max_placeholder() {
      const conditionTypeStr = this.get_condition_type_string(this.condition_form.condition_type);
      
      if (conditionTypeStr === 'time_based') {
        return 'YYYY-MM-DD (end date)';
      }
      
      return 'Enter end value';
    },

    get_value_hint() {
      const conditionTypeStr = this.get_condition_type_string(this.condition_form.condition_type);
      const operatorStr = this.get_operator_string(this.condition_form.operator);
      
      if (conditionTypeStr === 'time_based') {
        if (['greater_than', 'less_than', 'greater_or_equal', 'less_or_equal'].includes(operatorStr)) {
          return 'Enter date in YYYY-MM-DD format';
        }
        return 'For dates: YYYY-MM-DD. For days: Enter number';
      }
      
      if (['in', 'not_in'].includes(operatorStr)) {
        return 'Separate multiple values with commas (value1, value2, value3)';
      }
      
      return 'Enter the required value';
    },

    parse_options_string(optionsString) {
      if (!optionsString) return [];
      return optionsString.split(',')
        .map(option => option.trim())
        .filter(option => option.length > 0);
    },

    format_options_array(optionsArray) {
      if (!Array.isArray(optionsArray)) return "";
      return optionsArray.join(', ');
    },

    go_back() {
      this.$router.push({ name: 'ComplianceRules', query: { form_id: this.current_form_id, form_name: this.current_form_name } });
    },
  },
  beforeUnmount() {
    if (this.dataTable) {
      this.dataTable.destroy();
    }
    
    [this.conditionModal, this.deleteConditionModal].forEach(modal => {
      if (modal) modal.dispose();
    });
    
    this.show_assign_modal = false;
    this.condition_for_assignment = null;
    this.entity_type_id = null;
  },
};